// Phase HR-003 Wave 5 — EHS observation API client.
import apiClient from '@/shared/api/client'

// ── Fatigue ─────────────────────────────────────────────────────────

export type FatigueSign =
  | 'SLOW_LIFTING' | 'PAUSING' | 'YAWNING' | 'SWEATING'
  | 'COMPLAINT_VERBAL' | 'POSTURE_DROP' | 'ERRORS_RISING'

export interface FatigueCreate {
  plant_id: string
  department_id: string
  observation_date: string
  section_code?: string | null
  shift_id?: string | null
  hour_band?: string | null
  output_observed?: number | null
  output_unit?: string | null
  fatigue_signs?: FatigueSign[]
  temperature_c?: number | null
  humidity_pct?: number | null
  noise_db?: number | null
  ot_status?: boolean | null
  worker_complaints?: string | null
  photo_url?: string | null
  proposed_action?: string | null
}

export interface FatigueObservation extends FatigueCreate {
  id: string
  observation_at: string
  department_name?: string | null
  action_closed_at?: string | null
  captured_at: string
}

// ── Ergonomic ───────────────────────────────────────────────────────

export type PostureType =
  | 'STANDING' | 'BENT' | 'TWISTED' | 'OVERHEAD' | 'SEATED' | 'SQUATTING'
export type RiskMethod = 'RULA' | 'REBA' | 'OWAS' | 'SIMPLE'
export type RiskBand = 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH'

export interface ErgonomicCreate {
  plant_id: string
  department_id: string
  observation_date: string
  task_description: string
  section_code?: string | null
  machine_id?: string | null
  posture_observed?: PostureType | null
  lift_weight_kg?: number | null
  lift_frequency_per_hr?: number | null
  reach_distance_cm?: number | null
  seated_pct?: number | null
  risk_method?: RiskMethod | null
  risk_score?: number | null
  risk_band?: RiskBand | null
  photo_evidence_url?: string | null
  proposed_action?: string | null
}

export interface ErgonomicObservation extends ErgonomicCreate {
  id: string
  department_name?: string | null
  machine_code?: string | null
  implemented_date?: string | null
  implementation_evidence_url?: string | null
  captured_at: string
}

// ── Safety ──────────────────────────────────────────────────────────

export type SafetyObsType =
  | 'UNSAFE_ACT' | 'PPE_NON_COMPLIANCE' | 'NEAR_MISS' | 'GOOD_PRACTICE'

export type SafetyCategory =
  | 'FALL' | 'LIFT' | 'ROTATING_MACHINE' | 'CHEMICAL' | 'NOISE' | 'HEAT'
  | 'ELECTRICAL' | 'TRIP_SLIP' | 'HELMET' | 'GOGGLES' | 'SHOES' | 'GLOVES'
  | 'EAR_PLUG' | 'MASK' | 'HOUSEKEEPING' | 'EMERGENCY_EXIT'
  | 'TOOL_HANDLING' | 'OTHER'

export type SeverityPotential = 'LOW' | 'MEDIUM' | 'HIGH' | 'FATAL'

export interface SafetyCreate {
  plant_id: string
  observation_type: SafetyObsType
  description: string
  department_id?: string | null
  section_code?: string | null
  machine_id?: string | null
  category_code?: SafetyCategory | null
  observed_employee_id?: string | null
  severity_potential?: SeverityPotential | null
  photo_url?: string | null
  immediate_action_taken?: string | null
  proposed_action?: string | null
  action_due_at?: string | null
}

export interface SafetyObservation extends SafetyCreate {
  id: string
  observation_at: string
  department_name?: string | null
  machine_code?: string | null
  observed_employee_name?: string | null
  action_closed_at?: string | null
  action_closure_notes?: string | null
  captured_at: string
}

export interface SafetyStats {
  total: number
  by_type: Record<string, number>
  by_category: Record<string, number>
  by_severity: Record<string, number>
  open_count: number
  overdue_count: number
  avg_closure_hours: number | null
}

const BASE = '/hr/ehs'

export const ehsApi = {
  // Fatigue
  fatigueCreate: async (body: FatigueCreate): Promise<FatigueObservation> =>
    (await apiClient.post<FatigueObservation>(`${BASE}/fatigue`, body)).data,
  fatigueList: async (params?: {
    plant_id?: string; department_id?: string
    from_date?: string; to_date?: string; limit?: number
  }) => (await apiClient.get<FatigueObservation[]>(`${BASE}/fatigue`, { params })).data,

  // Ergonomic
  ergonomicCreate: async (body: ErgonomicCreate): Promise<ErgonomicObservation> =>
    (await apiClient.post<ErgonomicObservation>(`${BASE}/ergonomic`, body)).data,
  ergonomicList: async (params?: {
    plant_id?: string; department_id?: string; risk_band?: RiskBand
    from_date?: string; to_date?: string; limit?: number
  }) => (await apiClient.get<ErgonomicObservation[]>(`${BASE}/ergonomic`, { params })).data,
  ergonomicImplement: async (id: string, implementedDate: string, evidenceUrl?: string) =>
    (await apiClient.post<ErgonomicObservation>(
      `${BASE}/ergonomic/${id}/implement`,
      null,
      { params: {
        implemented_date: implementedDate,
        ...(evidenceUrl ? { implementation_evidence_url: evidenceUrl } : {}),
      } }
    )).data,

  // Safety
  safetyCreate: async (body: SafetyCreate): Promise<SafetyObservation> =>
    (await apiClient.post<SafetyObservation>(`${BASE}/safety`, body)).data,
  safetyList: async (params?: {
    plant_id?: string; department_id?: string
    observation_type?: SafetyObsType; category_code?: SafetyCategory
    severity_potential?: SeverityPotential; open_only?: boolean
    from_dt?: string; to_dt?: string; limit?: number
  }) => (await apiClient.get<SafetyObservation[]>(`${BASE}/safety`, { params })).data,
  safetyClose: async (id: string, closure_notes: string) =>
    (await apiClient.post<SafetyObservation>(
      `${BASE}/safety/${id}/close`,
      { action_closure_notes: closure_notes }
    )).data,
  safetyStats: async (params?: {
    plant_id?: string; department_id?: string
    from_dt?: string; to_dt?: string
  }) => (await apiClient.get<SafetyStats>(`${BASE}/safety/stats`, { params })).data,
}
